import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Activities.css';

/* Fallback image */
import img_mountain from '../assets/image/mountain_village.png';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/activites/`;

function ActivityDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await fetch(`${API_URL}${id}/`, {
          headers: {
            'Accept-Language': i18n.language || 'fr'
          }
        });
        if (res.ok) {
          const data = await res.json();
          // Extract photo URLs
          const photos = data.photos && data.photos.length > 0
            ? data.photos.map(p => p.image)
            : [img_mountain];
          setActivity({ ...data, photos });
        } else {
          // If not found or error, redirect back
          navigate('/activites');
        }
      } catch (err) {
        console.error("Erreur de chargement de l'activité:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [id, navigate, i18n.language]);

  if (loading) {
    return (
      <div className="act-details-loading">
        <div className="act-spinner"></div>
        <p>{t('activities.loading')}</p>
      </div>
    );
  }

  if (!activity) return null;

  const safePhotos = activity.photos && activity.photos.length > 0 ? activity.photos : [img_mountain];

  return (
    <div className="activities-page">
      {/* ── HERO BANNER ── */}
      <section 
        className="ap-hero" 
        style={{ backgroundImage: `url(${img_mountain})` }}
      >
        <div className="ap-hero-overlay"></div>
        <div className="ap-hero-content">
          <span className="ap-hero-badge">{t('activities.badgeHero')}</span>
          <h1 className="ap-hero-title">
            {activity.titre}
          </h1>
          <p className="ap-hero-sub">
            {t('activities.subHero')}
          </p>
        </div>
        <div className="ap-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="var(--background-solid)"
              d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="act-section">
        <div className="act-container">
          <div className="act-details-layout">
            
            {/* Left Column: Gallery & Description */}
            <div className="act-details-main">
              <Link to="/activites" className="act-details-back-link">
                {t('activities.backLink')}
              </Link>

              {/* Large Main Photo */}
              <div className="act-details-gallery">
                <div className="act-details-main-img-container">
                  <img 
                    src={safePhotos[currentPhoto]} 
                    alt={activity.titre} 
                    className="act-details-main-img" 
                  />
                </div>
                
                {/* Thumbnails if multiple photos */}
                {safePhotos.length > 1 && (
                  <div className="act-details-thumbs">
                    {safePhotos.map((photo, i) => (
                      <button
                        key={i}
                        className={`act-details-thumb-btn${i === currentPhoto ? ' active' : ''}`}
                        onClick={() => setCurrentPhoto(i)}
                      >
                        <img src={photo} alt={`Miniature ${i + 1}`} className="act-details-thumb-img" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="act-details-content">
                <h2 className="act-details-section-title">{t('activities.sectionTitle')}</h2>
                <p className="act-details-description">{activity.description}</p>
              </div>
            </div>

            {/* Right Column: Sidebar */}
            <aside className="act-details-sidebar">
              <div className="act-details-info-card">
                <span className="act-category-badge act-category-badge--large">
                  {activity.categorie.charAt(0).toUpperCase() + activity.categorie.slice(1)}
                </span>
                
                <h3 className="act-details-sidebar-title">{t('activities.infoTitle')}</h3>
                
                <ul className="act-details-info-list">
                  <li className="act-details-info-item">
                    <span className="act-details-info-label">{t('activities.infoLocation')}</span>
                    <span className="act-details-info-value">{t('activities.infoLocationVal')}</span>
                  </li>
                  <li className="act-details-info-item">
                    <span className="act-details-info-label">{t('activities.infoType')}</span>
                    <span className="act-details-info-value">{t('activities.infoTypeVal')}</span>
                  </li>
                </ul>

                <hr className="act-details-sidebar-divider" />

                <div className="act-details-cta-box">
                  <p dangerouslySetInnerHTML={{ __html: t('activities.ctaTextHtml') }} />
                  <Link to="/reservation" className="btn-primary act-details-cta-btn">
                    {t('activities.ctaBtn')}
                  </Link>
                </div>
              </div>
            </aside>

          </div>
        </div>
      </section>
    </div>
  );
}

export default ActivityDetails;
